// src/components/search/SearchBar.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { searchableTools } from '../../config/menuConfig';
import { useDebounce } from '../../hooks/useDebounce';
import {
    TextField, List, ListItemButton, ListItemText, Popover,
    InputAdornment, Box, ListItemIcon, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const SearchBar: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [query, setQuery] = useState('');
    const [results, setResults] = useState<typeof searchableTools>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const debouncedQuery = useDebounce(query, 300);

    useEffect(() => {
        if (debouncedQuery.trim() === '') {
            setResults([]);
            setIsOpen(false);
            return;
        }

        const lowerCaseQuery = debouncedQuery.toLowerCase();
        const filteredResults = searchableTools.filter(
            (tool) =>
                t(tool.nameKey, { ns: 'tools' }).toLowerCase().includes(lowerCaseQuery) ||
                t(tool.descKey, { ns: 'tools' }).toLowerCase().includes(lowerCaseQuery)
        );

        setResults(filteredResults);
        setIsOpen(true);
        setActiveIndex(-1);
    }, [debouncedQuery, t]);

    const handleClose = () => {
        setIsOpen(false);
        setAnchorEl(null);
    };

    const handleNavigate = (route: string) => {
        navigate(route);
        setQuery('');
        handleClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setQuery('');
            handleClose();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter' && activeIndex >= 0 && results[activeIndex]) {
            handleNavigate(results[activeIndex].route);
        }
    };

    return (
        <Box sx={{ position: 'relative', width: {xs: 150, md: 250} }}>
            <TextField
                variant="outlined" size="small" fullWidth
                placeholder={t('search_placeholder', { ns: 'common' })}
                value={query}
                onChange={(e) => {
                    setQuery(e.target.value);
                    if (!anchorEl) setAnchorEl(e.currentTarget);
                }}
                onKeyDown={handleKeyDown}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                }}
            />
            <Popover
                open={isOpen && query.length > 0}
                anchorEl={anchorEl}
                onClose={handleClose}
                disableAutoFocus disableEnforceFocus
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    style: { width: anchorEl ? anchorEl.clientWidth : undefined },
                }}
            >
                <List dense>
                    {results.length > 0 ? (
                        results.map((tool, index) => (
                            <ListItemButton
                                key={tool.id}
                                selected={index === activeIndex}
                                onClick={() => handleNavigate(tool.route)}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <tool.icon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t(tool.nameKey, { ns: 'tools' })}
                                    secondary={t(tool.categoryNameKey, { ns: 'tools' })}
                                />
                            </ListItemButton>
                        ))
                    ) : (
                        <Typography sx={{p: 2}} variant="body2" color="text.secondary">
                            {t('search_no_results', { ns: 'tools', query: debouncedQuery })}
                        </Typography>
                    )}
                </List>
            </Popover>
        </Box>
    );
};

export default SearchBar;